import {
    Component,
    OnInit,
    ViewContainerRef,
    ViewChild,
    Type,
    ComponentRef,
    createNgModule,
    Injector
} from '@angular/core';
import { Observable } from 'rxjs';
import { IMenuItem } from '../../interfaces';
import { PreferencesService } from '@shared/services';
import { takeWhile, filter, distinctUntilChanged } from 'rxjs/operators';
import { isNumber } from 'lodash';
import { selectReportsAndSelectedReport } from '@rpt-ngrx/selectors/reports.selector';
import { IPublishedReport, IReport, SelectedReport } from '@shared/types/report';
import { selectCompanies } from '@rpt-ngrx/selectors/session.selector';
import { set_menu_items } from '@rpt-ngrx/actions/ui.action';
import { selectMenuItems } from '@rpt-ngrx/selectors/ui.selector';
import { Store } from '@ngrx/store';
import { ICompany } from '@shared/types/session';
import { show_company_selector } from '@rpt-ngrx/actions/session.action';
import { XUXNavigationComponent } from '../../xux-navigation/xux-navigation.component';
import { ReportFilterComponent } from '@components/report-filters/report-filter.component';
import { ReportNavigatorService } from '@containers/reports/report/services/report-navigator.service';

@Component({
    selector: 'xux-drawer-sidebar',
    templateUrl: './xux-drawer-sidebar.component.html',
    styleUrls: ['./xux-drawer-sidebar.component.scss']
})
export class XUXDrawerSidebarComponent implements OnInit {
    // @ts-ignore
    @ViewChild('modalContainer', { read: ViewContainerRef }) modals;
    @ViewChild('navigationComponentPlaceholder', { read: ViewContainerRef, static: true })
    public navigationComponentPlaceholder: ViewContainerRef;
    public navigationComponent: Type<XUXNavigationComponent>;
    @ViewChild('filtersComponentPlaceholder', { read: ViewContainerRef, static: true })
    public filtersComponentPlaceholder: ViewContainerRef;
    public filtersComponent: Type<ReportFilterComponent>;

    public menuItems: IMenuItem[];
    public reportsState$: Observable<{reports: Array<IReport>, selectedReport: IReport}>;
    public companiesState$: Observable<Array<ICompany>>;
    public menuItems$: Observable<Array<IMenuItem>>;
    public reports: IReport[] = [];
    public needShowReports: boolean = true;
    public needShowFilters: boolean = false;
    public needShowBackToCompanies: boolean = false;
    private alive: boolean = true;

    private navigationComponentRef: ComponentRef<any> = null;
    private filtersComponentRef: ComponentRef<any> = null;

    constructor(
        private store: Store,
        private preferencesService: PreferencesService,
        private injector: Injector,
        private reportNavigator: ReportNavigatorService
    ) {
        this.reportsState$ = this.store.select(selectReportsAndSelectedReport);
        this.menuItems$ = this.store.select(selectMenuItems);
        this.companiesState$ = this.store.select(selectCompanies);
    }

    ngOnInit(): void {
        this.reportsState$.pipe(
            takeWhile(() => this.alive),
            filter(({reports}) => !!reports?.length),
            distinctUntilChanged((prev, next) =>
                prev.selectedReport?.id === next.selectedReport?.id &&
                prev.reports.length === next.reports.length
            ),
        ).subscribe(({reports, selectedReport}) => {
            this.prepareMenuItems(reports);
            this.needShowReports = !selectedReport;
            if (this.needShowReports) {
                this.createLazyNavigationComponent().then(() => {
                    if (this.filtersComponentRef) {
                        this.filtersComponentRef.destroy();
                        this.filtersComponentRef = null;
                        this.filtersComponentPlaceholder.detach();
                    }
                });
            } else {
                this.createLazyFiltersComponent().then(() => {
                    this.reportNavigator.bindFiltersRef(this.navigationComponentRef);
                    if (this.navigationComponentRef) {
                        this.navigationComponentRef.destroy();
                        this.navigationComponentRef = null;
                        this.navigationComponentPlaceholder.detach();
                    }
                });
            }
        });

        this.companiesState$.pipe(takeWhile(() => this.alive)).subscribe(companies => {
            this.needShowBackToCompanies = !!(companies && companies.length);
        });
    }

    private async createLazyNavigationComponent(): Promise<void> {
        const m = (await import('../../xux-navigation/xux-navigation.module')).XuxNavigationModule;
        const ngModuleRef = createNgModule(m, this.injector);
        const MyComponent = m.getMyComponent();
        this.navigationComponentRef = this.navigationComponentPlaceholder.createComponent(MyComponent, { ngModuleRef: ngModuleRef });
    }

    private async createLazyFiltersComponent(): Promise<void> {
        const m = (await import('../../../components/report-filters/report-filter.module')).ReportFilterModule;
        const ngModuleRef = createNgModule(m, this.injector);
        const MyComponent = m.getMyComponent();
        this.filtersComponentRef = this.filtersComponentPlaceholder.createComponent(MyComponent, { ngModuleRef: ngModuleRef });
        this.reportNavigator.bindFiltersViewRef(this.filtersComponentPlaceholder);

    }

    public showCompanySelector() {
        this.store.dispatch(show_company_selector());
    }

    private prepareMenuItems(reports: IReport[]) {
        this.reports = [ ...reports ];
        const newMenuItems: Array<IMenuItem> = [];
        this.reports.forEach((report: IReport | IPublishedReport) => {
            const reportItem = {
                id: report._id,
                title: report['custom_navigation_title'] || report.name,
                routing: report.url || '/custom-reports/report/' + (report._id ?? report.id),
                index: isNumber(report['index']) ? report['index'] : null
            };
            const menuItem = {
                title: this.getMenuItemTitle(report),
                index: isNumber(report['category_index']) ? report['category_index'] : null,
                active: false,
                parent_report_id: (report as IPublishedReport).parent_report_id || report.id,
                sub: [],
                category: report.category,
            };
            const categoryItem = newMenuItems.find(obj => obj.title === report['category']);

            if (!categoryItem) {
                menuItem.sub.push(reportItem);
                newMenuItems.push(menuItem);
            } else {
                categoryItem.sub.push(reportItem);
            }
            if (report.id === 'sales_detail_report') {
                this.checkAndSaveDayPartProperties(report);
            }
        });
        this.store.dispatch(set_menu_items({menuItems: newMenuItems}));
    }

    private getMenuItemTitle(report: IReport): string {
        if (report.isPublished) {
            return report['custom_navigation_title'];
        }
        if (report['category']) {
            return report['category'];
        }
        return report.name;
    }

    private checkAndSaveDayPartProperties(report: IReport) {
        if (report?.day_parts?.length && !this.preferencesService.dayPartOrderingPreferences) {
            this.preferencesService.setDayPartOrderingPreferences(report.day_parts);
        }
    }
}
